import { CookieItemKeyEnum } from '../enums/CookieItemKeyEnum';

const SECRET_KEY = import.meta.env.VITE_APP_CRYPTO_KEY || 'default';
const onChangeCallbacks: Array<
  (key: string, newValue: any, oldValue: any) => void
> = [];

const TEXT_ENCODER = new TextEncoder();
const TEXT_DECODER = new TextDecoder();

/**
 * @desc Get a cryptographic key from a secret key
 */
async function getCryptoKey(secretKey: string): Promise<CryptoKey> {
  const keyMaterial = await window.crypto.subtle.importKey(
    'raw',
    TEXT_ENCODER.encode(secretKey),
    { name: 'PBKDF2' },
    false,
    ['deriveKey']
  );
  return await window.crypto.subtle.deriveKey(
    {
      hash: 'SHA-256',
      iterations: 100000,
      name: 'PBKDF2',
      salt: TEXT_ENCODER.encode('some-salt'),
    },
    keyMaterial,
    { length: 256, name: 'AES-GCM' },
    false,
    ['encrypt', 'decrypt']
  );
}

/**
 * @desc Encrypt data using AES-GCM
 */
async function encryptData(
  data: string,
  key: CryptoKey
): Promise<{ data: number[]; iv: number[] }> {
  const iv = window.crypto.getRandomValues(new Uint8Array(12));
  const encrypted = await window.crypto.subtle.encrypt(
    {
      iv: iv,
      name: 'AES-GCM',
    },
    key,
    TEXT_ENCODER.encode(data)
  );
  return {
    data: Array.from(new Uint8Array(encrypted)),
    iv: Array.from(iv),
  };
}

/**
 * @desc Decrypt data using AES-GCM
 */
async function decryptData(
  encryptedData: { data: number[]; iv: number[] },
  key: CryptoKey
): Promise<string> {
  const iv = new Uint8Array(encryptedData.iv);
  const encryptedBytes = new Uint8Array(encryptedData.data);
  const decrypted = await window.crypto.subtle.decrypt(
    {
      iv: iv,
      name: 'AES-GCM',
    },
    key,
    encryptedBytes
  );
  return TEXT_DECODER.decode(decrypted);
}

/**
 * Registers a callback to be invoked when cookies change.
 * Note: Cookies do not have a native change event, so this is a placeholder.
 * A real change detection would require polling.
 * @param callback The callback to register.
 * @returns A function to remove the callback.
 */
export function onCookieChange(
  callback: (key: string, newValue: any, oldValue: any) => void
): () => void {
  onChangeCallbacks.push(callback);

  // Returns a function to remove the callback
  return () => {
    const index = onChangeCallbacks.indexOf(callback);
    if (index !== -1) {
      onChangeCallbacks.splice(index, 1);
    }
  };
}

/**
 * Invokes all registered callbacks with the new value.
 * @param key The key that has changed.
 * @param newValue The new value of the item.
 * @param oldValue The old value of the item.
 */
function invokeOnChangeCallbacks(
  key: string,
  newValue: any,
  oldValue: any
): void {
  onChangeCallbacks.forEach((callback) => callback(key, newValue, oldValue));
}

/**
 * Sets a cookie with the specified name and value, with optional attributes.
 * @param name The name of the cookie.
 * @param value The value of the cookie.
 * @param attributes Optional attributes for the cookie (expires, path, domain, secure, sameSite).
 */
export function setCookie(
  name: string,
  value: string,
  attributes: { [key: string]: any } = {}
): void {
  let cookieString = `${encodeURIComponent(name)}=${value}`;

  if (attributes.expires) {
    cookieString += `; expires=${attributes.expires.toUTCString()}`;
  }
  if (attributes.path) {
    cookieString += `; path=${attributes.path}`;
  }
  if (attributes.domain) {
    cookieString += `; domain=${attributes.domain}`;
  }
  if (attributes.secure) {
    cookieString += '; secure';
  }
  if (attributes.sameSite) {
    cookieString += `; samesite=${attributes.sameSite}`;
  }

  document.cookie = cookieString;
}

/**
 * Gets the value of a cookie by name.
 * @param name The name of the cookie.
 * @returns The value of the cookie, or null if not found.
 */
export function getCookie(name: string): string | null {
  const nameEQ = encodeURIComponent(name) + '=';
  const ca = document.cookie.split(';');
  for (let c of ca) {
    while (c.charAt(0) === ' ') c = c.substring(1, c.length);
    if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
  }
  return null;
}

/**
 * Deletes a cookie by name.
 * @param name The name of the cookie.
 * @param path Optional path of the cookie.
 */
function deleteCookie(name: string, path?: string): void {
  setCookie(name, '', {
    expires: new Date(0),
    path: path || '/',
  });
}

/**
 * Clears all cookies.
 */
function clearCookies(): void {
  const cookies = document.cookie.split(';');
  for (const cookie of cookies) {
    const eqPos = cookie.indexOf('=');
    const name = eqPos > -1 ? cookie.substr(0, eqPos) : cookie;
    deleteCookie(name.trim());
  }
}

/**
 * @desc Stores data (object or string) in cookies
 * @param key The key under which to store the data.
 * @param data The data to store (can be an object or a string).
 * @param attributes Optional attributes for the cookie (expires, path, domain, secure, sameSite).
 * @param encrypt Optional boolean to determine if the value should be encrypted (default: true).
 */
export async function storeCookieItem(
  key: CookieItemKeyEnum,
  data: any,
  attributes: { [key: string]: any } = {},
  encrypt: boolean = true
): Promise<void> {
  const oldValue = await getCookieItem(key, encrypt);
  let valueToStore: string;

  if (encrypt) {
    const stringData = typeof data === 'string' ? data : JSON.stringify(data);
    const cryptoKey = await getCryptoKey(SECRET_KEY);
    const encryptedData = await encryptData(stringData, cryptoKey);
    valueToStore = encodeURIComponent(JSON.stringify(encryptedData));
  } else {
    valueToStore = JSON.stringify(data);
  }

  setCookie(key, valueToStore, attributes);
  invokeOnChangeCallbacks(key, data, oldValue);
}

/**
 * @desc Retrieves data from cookies
 * @param key The key to retrieve data from.
 * @param encrypt Optional boolean to determine if the value is encrypted (default: true).
 * @returns The decrypted data, or null if not found.
 */
export async function getCookieItem(
  key: CookieItemKeyEnum,
  encrypt: boolean = true
): Promise<any | null> {
  const cookieValue = getCookie(key);
  if (!cookieValue) {
    return null;
  }

  try {
    if (encrypt) {
      const cryptoKey = await getCryptoKey(SECRET_KEY);
      const decryptedData = await decryptData(
        JSON.parse(decodeURIComponent(cookieValue)),
        cryptoKey
      );
      try {
        return JSON.parse(decryptedData);
      } catch {
        return decryptedData;
      }
    } else {
      try {
        return JSON.parse(cookieValue);
      } catch {
        return cookieValue;
      }
    }
  } catch (err) {
    console.error('Error reading cookie', err);
    return null;
  }
}

/**
 * @desc Deletes data from cookies
 * @param key The key of the data to delete.
 * @param encrypt Optional boolean to determine if the value is encrypted (default: true).
 */
export async function deleteCookieItem(
  key: CookieItemKeyEnum,
  encrypt: boolean = true
): Promise<void> {
  const oldValue = await getCookieItem(key, encrypt);
  deleteCookie(key);
  invokeOnChangeCallbacks(key, null, oldValue);
}

/**
 * @desc Clears all data from cookies
 */
export function clearAllCookies(): void {
  clearCookies();
}
